
import React, { useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, useLocation } from "react-router-dom";
import { Navbar } from "@/components/common/Navbar";
import { Footer } from "@/components/common/Footer";
import { FloatingButtons } from "@/components/common/FloatingButtons";
import Index from "./pages/Index";
import LoanProducts from "./pages/LoanProducts";
import Calculators from "./pages/Calculators";
import SuccessStories from "./pages/SuccessStories";
import Resources from "./pages/Resources";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import NotFound from "./pages/NotFound";

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top when pathname changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  return null;
};

// Layout component to wrap all routes
const RootLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex flex-col text-balance">
    <ScrollToTop />
    <Navbar />
    <main className="flex-1 w-full overflow-x-hidden">
      <div className="w-full max-w-[100vw] overflow-x-hidden">
        {children}
      </div>
    </main>
    <Footer />
  </div>
);

const queryClient = new QueryClient();

// Configure router with future flags
const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <RootLayout><Index /></RootLayout>,
    },
    {
      path: "/loan-products",
      element: <RootLayout><LoanProducts /></RootLayout>,
    },
    {
      path: "/calculators",
      element: <RootLayout><Calculators /></RootLayout>,
    },
    {
      path: "/success-stories",
      element: <RootLayout><SuccessStories /></RootLayout>,
    },
    {
      path: "/resources",
      element: <RootLayout><Resources /></RootLayout>,
    },
    {
      path: "/about",
      element: <RootLayout><About /></RootLayout>,
    },
    {
      path: "/contact",
      element: <RootLayout><Contact /></RootLayout>,
    },
    {
      path: "/privacy-policy",
      element: <RootLayout><PrivacyPolicy /></RootLayout>,
    },
    {
      path: "/terms-and-conditions",
      element: <RootLayout><TermsAndConditions /></RootLayout>,
    },
    {
      path: "*",
      element: <RootLayout><NotFound /></RootLayout>,
    },
  ]
);

// Main App component
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <RouterProvider router={router} />
        <FloatingButtons />
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
